<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="520px"
    custom-class="ca-binding-dialog"
    :close-on-click-modal="false"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div slot="title" class="ca-binding-dialog__title">
      <i class="el-icon-key ca-binding-dialog__icon" />
      <span>CA信息认证</span>
    </div>

    <!-- 第一步：CA 登录（插入上海CA UKey 后输入 CA 密码读取证书） -->
    <el-form
      v-if="step === 0"
      ref="loginForm"
      class="ca-binding-dialog__form"
      :model="form"
      label-width="90px"
      @submit.native.prevent
    >
      <el-form-item label="CA密码">
        <el-input
          v-model="form.password"
          type="password"
          show-password
          placeholder="请输入CA密码"
          @keyup.enter.native="handleLogin"
        />
      </el-form-item>
      <p class="ca-binding-dialog__tip">
        <i class="el-icon-info" />
        请先插入上海CA UKey，输入CA密码后点击「CA信息获取」读取证书信息。
      </p>
    </el-form>

    <!-- 第二步：登录成功，展示证书信息（机构名称 / 证书类型 / 证书有效期） -->
    <div v-else class="ca-binding-dialog__cert">
      <p class="ca-binding-dialog__row">
        <span class="ca-binding-dialog__label">CA序列号：</span>{{ certInfo.serialNumber || '--' }}
      </p>
      <p class="ca-binding-dialog__row">
        <span class="ca-binding-dialog__label">机构名称：</span>{{ certInfo.subjectCN || '--' }}
      </p>
      <p class="ca-binding-dialog__row">
        <span class="ca-binding-dialog__label">证书类型：</span>{{ certTypeText }}
      </p>
      <p class="ca-binding-dialog__row">
        <span class="ca-binding-dialog__label">证书有效期：</span>
        {{ certInfo.certBeginDate || '--' }} ~ {{ certInfo.certEndDate || '--' }}
      </p>
    </div>

    <div slot="footer">
      <el-button @click="close">取消</el-button>
      <el-button v-if="step === 0" type="danger" :loading="logining" @click="handleLogin">
        CA信息获取
      </el-button>
      <el-button v-else type="danger" :loading="submitting" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { shcaAuthRenew } from '@/api/ca'
import ShecaCa, { CERT_TYPE_TEXT } from '../util/shecaCa'

/**
 * 上海CA续签 · CA 信息认证弹框。
 * 流程：输入 CA 密码登录（读取 UKey 证书）→ 展示机构名称 / 证书类型 / 证书有效期 →
 * 点「确定」携带证书序列号 caSerialNo 调 shcaAuthRenew 换取认证 URL，由父页面新窗口打开。
 * 仅上海CA续签使用；上海CA申请无需读取证书序列号，走原有直接换链逻辑。
 */
export default {
  name: 'CaBindingDialog',
  data() {
    return {
      dialogVisible: false,
      orderId: '', // 当前续签订单 id（open 时传入）
      step: 0, // 0 待登录 / 1 已登录（展示证书信息）
      form: { password: '' },
      certInfo: {},
      ca: null, // ShecaCa 实例（登录成功后持有）
      logining: false,
      submitting: false,
    }
  },
  computed: {
    certTypeText() {
      return CERT_TYPE_TEXT[this.certInfo.certType] || '--'
    },
  },
  methods: {
    /**
     * 打开弹框（父组件通过 ref 调用）。
     * @param {string|number} orderId CA 订单 id
     */
    open(orderId) {
      this.orderId = orderId
      this.dialogVisible = true
    },
    // 每次打开重置登录态（UKey 可能已更换，必须重新读证书）
    handleOpen() {
      this.step = 0
      this.form.password = ''
      this.certInfo = {}
      this.ca = null
    },
    handleClosed() {
      this.ca = null
    },
    close() {
      this.dialogVisible = false
    },
    /**
     * CA 登录：校验浏览器环境 → 加载 SDK → 驱动服务探测 → 密码校验 → 读证书信息。
     */
    async handleLogin() {
      if (this.logining) return
      if (!this.form.password) {
        this.$message.warning('请输入CA密码')
        return
      }
      this.logining = true
      try {
        const ca = new ShecaCa(this.form.password)
        await ca.loadDriver()
        await ca.init()
        this.certInfo = await ca.getCertInfo()
        console.log('this.certInfo', this.certInfo)
        this.ca = ca
        this.step = 1
      } catch (e) {
        this.$message.error((e && e.message) || 'CA登录失败，请检查CA是否插入')
      } finally {
        this.logining = false
      }
    },
    /**
     * 确定：携带证书序列号（caSerialNo，续签接口必传）调上海CA续签下单，返回认证 URL。
     * 由父页面监听 success 打开认证 H5（与申请页的 openSHECAAuth 行为一致）。
     */
    handleConfirm() {
      if (this.submitting) return
      if (!this.certInfo.serialNumber) {
        this.$message.error('未读取到证书序列号，请插入UKey')
        return
      }
      this.submitting = true
      shcaAuthRenew({
        orderId: this.orderId,
        frontRedirectUrl: window.location.href,
        caSerialNo: this.certInfo.serialNumber,
      })
        .then((res) => {
          const url = res.data && res.data.authUrl
          // authUrl 缺失不是接口报错，拦截器不会提示，这里单独兜底
          if (!url) {
            this.$message.error('操作失败，请重试')
            return
          }
          this.dialogVisible = false
          this.$emit('success', url)
        })
        .catch(() => {
          // 业务错误提示由 http 拦截器统一 toast，这里仅兜住 rejection
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>
